import React from "react";
import get from "lodash/get";
import cloneDeep from "lodash/cloneDeep";
import moment from "moment-timezone";
import { connect } from "react-redux";
import { debounce } from "lodash";
import {
  Alert,
  Icon,
  Layout,
  Input,
  Button,
  Table,
  Switch,
  Modal,
  Badge,
  message,
  Tooltip,
  Select,
  Checkbox,
} from "antd";

import AdminHeader from "../../components/AdminHeader/AdminHeader";
import Footer from "../../components/Footer/Footer";
import classes from "./AuditLogs.css";

import isAuthenticated from "../../utils/isAuthenticated";
import {
  getAuditLogsRequest,
  deleteAuditLogsRequest,
  activateAuditLogsRequest,
  unreviewedAuditLogsRequest,
  reviewedAuditLogsRequest,
  resendAuditLogsRequest,
  getSitesRequest,
} from "../../api/requests";
import { logout } from "../../reducers/user/actions";
import {
  getAuditLogDeletePermission,
  getAuditLogUpdatePermission,
  getAuditLogReviewPermission,
  getUserTimezone,
  getUserShortTimezone,
} from "../../reducers/user/selectors";
import AuditForm from "../../components/AuditForm/AuditForm";
import { documentTypes } from "../../components/DocumentForm/DocumentForm";
import actionTypes from "./actionTypes";

const { Content } = Layout;
const { Option } = Select;

const errorLevelColors = {
  none: "#52c41a",
  low: "#c4c400",
  medium: "#c49904",
  high: "#ff2702",
  critical: "#c40200",
};

class AuditLogs extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      audits: [],
      nameSearch: null,
      errorMessage: null,
      responseMessage: null,
      isLoading: false,
      sites: [],
      site: null,
      filter: null,
      order: "desc",
      currentPage: 1,
      total: 1,
      pageSize: 25,
      eventVisibleDropdown: false,
      sortedInfo: {},
      auditEdit: null,
      filterBy: null,
      filterValue: null,
      filtersBy: [],
      filtersValue: [],
      approvedSelected: null,
    };
  }

  async componentDidMount() {
    this.onNameSearch();

    try {
      const { data } = await getSitesRequest();
      this.setState({ sites: data });
    } catch (e) {
      if (e.response.status === 401) {
        this.props.logout();
        return;
      }
      console.log(e);
    }
  }

  onNameSearch = async (value) => {
    this.setState(
      { nameSearch: value, filter: null, site: null },
      this.getAudits
    );
  };

  getAudits = async (
    page = this.state.currentPage,
    per_page = this.state.pageSize
  ) => {
    this.setState({
      errorMessage: null,
      isLoading: true,
      responseMessage: null,
    });

    try {
      const { data } = await getAuditLogsRequest({
        nameSearch: this.state.nameSearch,
        order_direction: this.state.order,
        page,
        per_page,
        order_column: get(this.state, "sortedInfo.columnKey"),
        filter_by: this.state.filtersBy,
        filter_value: this.state.filtersValue,
        filter_approve: this.state.approvedSelected,
      });

      if (Array.isArray(data.data)) {
        this.setState({
          audits: data.data,
          total: data.total_pages * per_page,
          currentPage: page,
        });
      } else {
        this.setState({
          audits: [data],
          total: 1 * per_page,
          currentPage: page,
        });
      }
    } catch (e) {
      if (get(e, "response.status") === 401) {
        this.props.logout();
        return;
      }
      this.setState({
        audits: null,
        errorMessage: get(e, "response.data.error", "Error"),
      });
    }

    this.setState({ isLoading: false });
  };

  clearFilters = () => {
    this.setState(
      {
        nameSearch: null,
        site: null,
        filter: null,
        filterValue: null,
        filterBy: null,
        sortedInfo: null,
        approvedSelected: null,
      },
      this.getAudits
    );
  };

  handleFilterChange = (index) => (value) => {
    this.setState((state) => {
      const filtersBy = cloneDeep(state.filtersBy);
      const filtersValue = cloneDeep(state.filtersValue);
      filtersBy[index] = value;
      delete filtersValue[index];
      return { filtersBy, filtersValue };
    });
  };

  handleFilterValueChange = (index) =>
    debounce((value) => {
      this.setState((state) => {
        const filtersValue = cloneDeep(state.filtersValue);
        filtersValue[index] = value;
        return { filtersValue };
      }, this.getAudits);
    }, 500);

  clearFilter = (index) => () => {
    this.setState((state) => {
      const filtersBy = cloneDeep(state.filtersBy);
      const filtersValue = cloneDeep(state.filtersValue);

      filtersBy.splice(index, 1);
      filtersValue.splice(index, 1);

      return { filtersBy, filtersValue };
    }, this.getAudits);
  };

  onSelectApprovedStatus = (approvedStatusType) => () => {
    let status;
    const { approvedSelected } = this.state;

    if (approvedSelected === null && approvedStatusType === "approved") {
      status = false;
    }
    if (approvedSelected === true && approvedStatusType === "approved") {
      status = false;
    }
    if (approvedSelected === false && approvedStatusType === "approved") {
      status = null;
    }

    if (approvedSelected === null && approvedStatusType === "unapproved") {
      status = true;
    }
    if (approvedSelected === false && approvedStatusType === "unapproved") {
      status = true;
    }
    if (approvedSelected === true && approvedStatusType === "unapproved") {
      status = null;
    }

    this.setState((_) => ({ approvedSelected: status }), this.getAudits);
  };

  handleTableChange = (pagination, filters, sorter) => {
    this.setState(
      {
        isLoading: true,
        pageSize: pagination.pageSize,
        sortedInfo: sorter,
        order:
          get(sorter, "order") === "ascend"
            ? "asc"
            : get(sorter, "order") === "descend"
            ? "desc"
            : "asc",
      },
      () => this.getAudits(pagination.current, pagination.pageSize)
    );
  };

  onAuditEdit = (audit) => () => {
    this.setState({ auditEdit: audit });
  };

  onAuditEditHide = () => {
    this.setState({ auditEdit: null });
  };

  onAuditEditSubmit = () => {
    this.setState({ auditEdit: null }, this.getAudits);
  };

  onSwitchAuditActivation = (id, state) => async () => {
    try {
      if (state === "active") {
        await deleteAuditLogsRequest(id);
      } else {
        await activateAuditLogsRequest(id);
      }

      message.success("Saved");

      this.getAudits();
    } catch (e) {
      if (get(e, "response.status") === 401) {
        this.props.logout();
        return;
      }
      message.error("Error");
      console.log(e);
    }
  };

  onResendClick = (id) => async () => {
    try {
      const result = await resendAuditLogsRequest(id);
      const msg = get(result, "data.message", "Email resent.");

      message.success(msg);

      this.getAudits();
    } catch (e) {
      if (get(e, "response.status") === 401) {
        this.props.logout();
        return;
      }
      message.error(get(e, "response.data.error", "Error!"));
      console.log(e);
    }
  };

  onSwitchAuditReview = (id, reviewed) => async () => {
    try {
      if (reviewed) {
        await unreviewedAuditLogsRequest(id);
      } else {
        await reviewedAuditLogsRequest(id);
      }

      message.success("Saved");

      this.getAudits();
    } catch (e) {
      if (get(e, "response.status") === 401) {
        this.props.logout();
        return;
      }
      message.error("Error");
      console.log(e);
    }
  };

  renderFilterItem = (index = this.state.filtersBy.length) => {
    const { filtersBy, filtersValue, sites } = this.state;

    return (
      <div className={classes.wrapperFilterItem} key={filtersBy[index]}>
        <div className={classes.searchWrapper}>
          <div className={classes.searchWrapperFilter}>
            <label className={classes.searchWrapperFilterItem}>
              <b className={classes.searchWrapperLabel}>Filter by</b>
              <Select
                onChange={this.handleFilterChange(index)}
                value={filtersBy[index]}
                style={{ width: "100%" }}
                placeholder="Select"
              >
                <Option
                  value="action_type"
                  disabled={filtersBy.includes("action_type")}
                >
                  Action type
                </Option>
                <Option
                  value="error_level"
                  disabled={filtersBy.includes("error_level")}
                >
                  Error level
                </Option>
                <Option value="result" disabled={filtersBy.includes("result")}>
                  Result
                </Option>
                <Option value="state" disabled={filtersBy.includes("state")}>
                  State
                </Option>
                <Option value="site" disabled={filtersBy.includes("site")}>
                  Site
                </Option>
                <Option value="user" disabled={filtersBy.includes("user")}>
                  User
                </Option>
                <Option
                  value="audit_log_email_type"
                  disabled={filtersBy.includes("audit_log_email_type")}
                >
                  Audit log email type
                </Option>
              </Select>
            </label>
          </div>

          {filtersBy[index] === "action_type" && (
            <label className={classes.searchWrapperFilterItem}>
              <b className={classes.searchWrapperLabel}>Result</b>
              <Select
                mode="multiple"
                showSearch
                onChange={this.handleFilterValueChange(index)}
                value={filtersValue[index]}
                style={{ width: "100%" }}
                placeholder="Select"
              >
                {Object.keys(actionTypes).map((key) => (
                  <Option key={key} value={key}>
                    {actionTypes[key]}
                  </Option>
                ))}
              </Select>
            </label>
          )}

          {filtersBy[index] === "error_level" && (
            <label className={classes.searchWrapperFilterItem}>
              <b className={classes.searchWrapperLabel}>Result</b>
              <Select
                mode="multiple"
                onChange={this.handleFilterValueChange(index)}
                value={filtersValue[index]}
                style={{ width: "100%" }}
                placeholder="Select"
              >
                <Option value="none">None</Option>
                <Option value="low">Low</Option>
                <Option value="medium">Medium</Option>
                <Option value="high">High</Option>
                <Option value="critical">Critical</Option>
              </Select>
            </label>
          )}

          {filtersBy[index] === "result" && (
            <label className={classes.searchWrapperFilterItem}>
              <b className={classes.searchWrapperLabel}>Result</b>
              <Select
                onChange={this.handleFilterValueChange(index)}
                value={filtersValue[index]}
                style={{ width: "100%" }}
                placeholder="Select"
              >
                <Option value="success">Success</Option>
                <Option value="failure">Failure</Option>
              </Select>
            </label>
          )}

          {filtersBy[index] === "state" && (
            <label className={classes.searchWrapperFilterItem}>
              <b className={classes.searchWrapperLabel}>State</b>
              <Select
                onChange={this.handleFilterValueChange(index)}
                value={filtersValue[index]}
                style={{ width: "100%" }}
                placeholder="Select"
              >
                <Option value="active">Active</Option>
                <Option value="inactive">Inactive</Option>
              </Select>
            </label>
          )}

          {filtersBy[index] === "audit_log_email_type" && (
            <label className={classes.searchWrapperFilterItem}>
              <b className={classes.searchWrapperLabel}>Result</b>
              <Select
                onChange={this.handleFilterValueChange(index)}
                value={filtersValue[index]}
                style={{ width: "100%" }}
                placeholder="Select"
              >
                <Option value="sent">Sent</Option>
                <Option value="not_sent">Not sent</Option>
              </Select>
            </label>
          )}

          {filtersBy[index] === "site" && (
            <label className={classes.searchWrapperFilterItem}>
              <b className={classes.searchWrapperLabel}>Site</b>
              <Select
                mode="multiple"
                onChange={this.handleFilterValueChange(index)}
                value={filtersValue[index]}
                style={{ width: "100%" }}
                placeholder="Select"
              >
                {sites.map((site) => (
                  <Option key={site.id} value={site.id}>
                    {site.code} - {site.name}
                  </Option>
                ))}
                <Option value={null}>None</Option>
              </Select>
            </label>
          )}

          {filtersBy[index] === "user" && (
            <label className={classes.searchWrapperFilterItem}>
              <b className={classes.searchWrapperLabel}>User</b>
              <Input
                onChange={(e) =>
                  this.handleFilterValueChange(index)(e.target.value)
                }
                style={{ width: "100%" }}
                placeholder="User"
              />
            </label>
          )}

          {filtersValue[index] && (
            <span
              className={classes.clearButton}
              onClick={this.clearFilter(index)}
            >
              Remove
            </span>
          )}
        </div>
      </div>
    );
  };

  render() {
    const {
      errorMessage,
      isLoading,
      responseMessage,
      nameSearch,
      audits,
      currentPage,
      total,
      pageSize,
      auditEdit,
      sortedInfo,
      filtersBy,
      filtersValue,
      sites,
      approvedSelected,
    } = this.state;

    const columns = [
      {
        dataIndex: "id",
        key: "id",
        title: "State/ Reviewed",
        render: (val, record) => (
          <div className={classes.nameAuditWrapper}>
            <div style={{ width: "86px" }}>
              <Badge
                count={record.state}
                style={{
                  backgroundColor:
                    record.state === "active" ? "#52c41a" : "#777",
                }}
              />
              &nbsp;
              <Badge
                count={record.reviewed ? "reviewed" : "unreviewed"}
                style={{
                  backgroundColor: record.reviewed ? "#52c41a" : "#777",
                }}
              />
            </div>
          </div>
        ),
      },
      {
        dataIndex: "site",
        key: "site",
        title: "Site",
        render: (site) => get(site, "name"),
        sorter: true,
        sortOrder: get(sortedInfo, "columnKey") === "site" && sortedInfo.order,
      },
      {
        dataIndex: "result",
        key: "result",
        title: "Result",
        render: (val, audit) => (
          <Tooltip placement="topLeft" title={audit.message}>
            <Badge
              count={val}
              style={{
                backgroundColor: val === "success" ? "#52c41a" : "#f00",
              }}
            />
          </Tooltip>
        ),
        sorter: true,
        sortOrder:
          get(sortedInfo, "columnKey") === "result" && sortedInfo.order,
      },
      {
        dataIndex: "error_level",
        key: "error_level",
        title: "Error level",
        sorter: true,
        render: (val) => (
          <Badge
            count={val}
            style={{ backgroundColor: errorLevelColors[val] }}
          />
        ),
        sortOrder:
          get(sortedInfo, "columnKey") === "error_level" && sortedInfo.order,
      },
      {
        dataIndex: "user",
        key: "user",
        title: "User",
        render: (user) => (
          <div
            style={{
              maxWidth: "200px",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {get(user, "username")}
          </div>
        ),
        sorter: true,
        sortOrder: get(sortedInfo, "columnKey") === "user" && sortedInfo.order,
      },
      {
        dataIndex: "action_type",
        key: "action_type",
        title: "Action type",
        render: (val) => val.replace(/_/g, " "),
        sorter: true,
        sortOrder:
          get(sortedInfo, "columnKey") === "action_type" && sortedInfo.order,
      },
      {
        dataIndex: "created",
        key: "created",
        title: "Created",
        render: (val) => (
          <span>
            {moment
              .utc(val)
              .tz(this.props.userTimezone || "UTC")
              .format("YYYY-MM-DD, HH:mm")}{" "}
            &nbsp;{this.props.userShortTimezone || "UTC"}
          </span>
        ),
        sorter: true,
        sortOrder:
          get(sortedInfo, "columnKey") === "created" && sortedInfo.order,
      },
      {
        dataIndex: "id",
        key: "id_actions",
        title: "Actions",
        render: (val, audit) => (
          <div className={classes.actions}>
            <Tooltip placement="topLeft" title="Edit Audit">
              <Icon
                type="edit"
                theme="filled"
                className={classes.iconEdit}
                onClick={this.onAuditEdit(audit)}
              />
            </Tooltip>
            &nbsp; &nbsp;
            <div style={{ width: "60px", display: "flex" }}>
              <div>
                <b className={classes.switchLabel}>State</b>
                <br />
                <Tooltip
                  placement="topLeft"
                  title="Activate / Deactivate Audit"
                >
                  <Switch
                    disabled={
                      (!this.props.auditLogUpdatePermission &&
                        audit.state === "inactive") ||
                      (!this.props.auditLogDeletePermission &&
                        audit.state === "active")
                    }
                    className={classes.reactivateBtn}
                    checked={audit.state === "active"}
                    onChange={this.onSwitchAuditActivation(val, audit.state)}
                  />
                </Tooltip>
              </div>
            </div>
            &nbsp; &nbsp;
            <div style={{ width: "60px", display: "flex" }}>
              <div>
                <b className={classes.switchLabel}>Reviewed</b>
                <br />
                <Tooltip
                  placement="topLeft"
                  title="Reviewed / Unreviewed Audit"
                >
                  <Switch
                    disabled={!this.props.auditLogReviewPermission}
                    className={classes.reactivateBtn}
                    checked={audit.reviewed}
                    onChange={this.onSwitchAuditReview(val, audit.reviewed)}
                  />
                </Tooltip>
              </div>
            </div>
            {audit.action_type.endsWith("_email") && (
              <div style={{ width: "60px", display: "flex" }}>
                <Tooltip placement="topLeft" title="Resend email notification">
                  <Button
                    type="primary"
                    size="small"
                    className={classes.resendBtn}
                    onClick={this.onResendClick(audit.id)}
                  >
                    Resend
                  </Button>
                </Tooltip>
              </div>
            )}
          </div>
        ),
      },
    ];

    return (
      <div style={{ backgroundColor: "#FAFAFA" }}>
        <Layout className={classes.parentContainer}>
          <AdminHeader />
          <Content className={classes.content}>
            <h3 className="headline-default">Audit logs</h3>

            <h4 className="headline-default">Filter</h4>
            <div className={classes.wrapperFilters}>
              <div className={classes.searchWrapperFilterItemApprove}>
                <Checkbox
                  onChange={this.onSelectApprovedStatus("approved")}
                  checked={
                    approvedSelected === null || approvedSelected === true
                  }
                >
                  Reviewed
                </Checkbox>
                <Checkbox
                  onChange={this.onSelectApprovedStatus("unapproved")}
                  checked={!approvedSelected}
                >
                  Unreviewed
                </Checkbox>
              </div>

              {filtersBy.map((_, index) => this.renderFilterItem(index))}

              {filtersBy.length === filtersValue.length &&
                filtersBy.length !== 5 &&
                this.renderFilterItem()}
            </div>

            {errorMessage && <Alert type="error" message={errorMessage} />}

            {responseMessage && (
              <Alert type="success" message={responseMessage} />
            )}

            <Table
              columns={columns}
              dataSource={audits}
              loading={isLoading}
              rowKey="id"
              onChange={this.handleTableChange}
              className={classes.table}
              pagination={{
                onShowSizeChange: (current, size) => {
                  this.setState({ pageSize: size });
                },
                current: currentPage,
                total,
                pageSize,
                showSizeChanger: true,
                pageSizeOptions: ["10", "25", "50", "100"],
              }}
            />

            {/*{isLoading && <Icon type="loading" />}*/}

            <Modal
              visible={!!auditEdit}
              onCancel={this.onAuditEditHide}
              footer={null}
            >
              {auditEdit && (
                <AuditForm
                  {...auditEdit}
                  isNew={auditEdit && !auditEdit.id}
                  onSubmit={this.onAuditEditSubmit}
                  onCancel={this.onAuditEditHide}
                />
              )}
            </Modal>
          </Content>
          <Footer noAdminLink />
        </Layout>
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  userTimezone: getUserTimezone(state),
  userShortTimezone: getUserShortTimezone(state),
  auditLogDeletePermission: getAuditLogDeletePermission(state),
  auditLogUpdatePermission: getAuditLogUpdatePermission(state),
  auditLogReviewPermission: getAuditLogReviewPermission(state),
});
const mapDispatchToProps = { logout };

export default isAuthenticated(
  connect(mapStateToProps, mapDispatchToProps)(AuditLogs)
);



// WEBPACK FOOTER //
// ./src/containers/AuditLogs/AuditLogs.js